/**
 * AccountModal — single source of truth for account management.
 *
 * Fullscreen responsive Dialog. Desktop: 224px sidebar with section buttons.
 * Mobile (<md): top row of flex-1 tab buttons. Content area renders one
 * section at a time via conditional render (no route changes — keeps the
 * underlying lobby/game layout mounted with its WebSockets intact).
 *
 * Profile section uses debounced auto-save. On close, any pending debounce
 * is flushed synchronously so the user never loses in-flight edits.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Gift, KeyRound, Lock, Mail, Trash2, UserCircle2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { auth } from '@/lib/firebase';
import { useAuthStore } from '@/lib/stores/authStore';
import { ProfileSection, type ProfileSectionHandle } from './sections/ProfileSection';
import { EmailSection } from './sections/EmailSection';
import { PasswordSection } from './sections/PasswordSection';
import { PasskeysSection } from './sections/PasskeysSection';
import { InviteFriendsSection } from './sections/InviteFriendsSection';
import { DangerSection } from './sections/DangerSection';
import { type AccountSection } from '@/lib/stores/accountModalStore';

interface SectionDef {
  id: AccountSection;
  label: string;
  icon: typeof UserCircle2;
}

interface AccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tab to open on. Defaults to 'profile'. Applied each time `open` flips true. */
  initialSection?: AccountSection;
}

export function AccountModal({ open, onOpenChange, initialSection }: AccountModalProps) {
  const [section, setSection] = useState<AccountSection>('profile');
  const profileRef = useRef<ProfileSectionHandle>(null);

  // Subscribe to the auth store so provider changes made WHILE the modal is open
  // (e.g. a guest converting to email+password via SaveAccountCard in the Invite
  // tab) recompute the section list live, not just on reopen. `setUser` produces
  // a fresh DTO ref on every auth change, so this fires on convert/email change.
  const storeUser = useAuthStore((s) => s.user);

  // Password section is hidden for passkey-only accounts (no password provider).
  // Dynamic so a user who adds a password later can still see the section.
  const hasPasswordProvider = useMemo(
    () => auth.currentUser?.providerData.some((p) => p.providerId === 'password') ?? false,
    // `auth.currentUser` is a stable ref but providerData changes after linking;
    // recompute on open and whenever the store user changes to pick that up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, storeUser]
  );

  const sections: SectionDef[] = useMemo(() => {
    const out: SectionDef[] = [
      { id: 'invite', label: 'Invite', icon: Gift },
      { id: 'profile', label: 'Profile', icon: UserCircle2 },
      { id: 'email', label: 'Email', icon: Mail },
    ];
    if (hasPasswordProvider) out.push({ id: 'password', label: 'Password', icon: Lock });
    out.push({ id: 'passkeys', label: 'Passkeys', icon: KeyRound });
    out.push({ id: 'danger', label: 'Delete', icon: Trash2 });
    return out;
  }, [hasPasswordProvider]);

  // If the previously-selected section disappears (e.g. user linked a
  // password and password section just appeared, or vice versa), fall back
  // to profile rather than rendering nothing.
  useEffect(() => {
    if (!sections.some((s) => s.id === section)) {
      setSection('profile');
    }
  }, [sections, section]);

  // On open, land on the requested tab (deep-link) or fall back to profile, so
  // the user doesn't land on a stale section from a previous open. `initialSection`
  // is a stable primitive, so manual in-modal tab switches persist (the effect
  // only re-runs when `open` flips or a new target section is requested).
  useEffect(() => {
    if (open) setSection(initialSection ?? 'profile');
  }, [open, initialSection]);

  // Flush pending debounced saves before letting Radix close the dialog.
  const handleOpenChange = useCallback(
    async (next: boolean) => {
      if (!next) {
        // Wait for any in-flight debounced save to fire + resolve.
        await profileRef.current?.flushPendingSaves();
      }
      onOpenChange(next);
    },
    [onOpenChange]
  );

  const handleCloseClick = useCallback(() => {
    void handleOpenChange(false);
  }, [handleOpenChange]);

  // Sections stay mounted while the modal is open so switching between them
  // doesn't drop in-flight state: debounced display-name saves in Profile and
  // the "Verification sent — check <email>" pending-state UI in Email.
  // Inactive sections are hidden with CSS rather than unmounted.
  const sectionWrapClass = (id: AccountSection) => (section === id ? '' : 'hidden');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        // Slide in from the left like the sidebar drawer (no center zoom/pop).
        motionClassName="duration-300 data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left"
        className={cn(
          'fixed inset-0 left-0 top-0 z-50 grid h-dvh w-screen max-w-none translate-x-0 translate-y-0',
          'gap-0 rounded-none border-0 p-0',
          // Hide the stock close button rendered by DialogContent
          '[&>button.absolute]:hidden',
          // Layout: mobile = column (tab row on top), md+ = row (sidebar left)
          'grid-rows-[auto_1fr] md:grid-cols-[14rem_1fr] md:grid-rows-1'
        )}
        onEscapeKeyDown={(e) => {
          // Let Radix handle the actual close — but if the profile has pending
          // saves we still want them flushed. onOpenChange will fire next tick.
          e.preventDefault();
          void handleOpenChange(false);
        }}
        onInteractOutside={(e) => {
          // Fullscreen layout means there's no visible overlay to click, but
          // cover the path for accessibility tools / browser dismiss gestures
          // so the async flush runs before Radix closes.
          e.preventDefault();
          void handleOpenChange(false);
        }}
      >
        <DialogTitle className="sr-only">Account settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage your profile, email, password, passkeys, and account deletion.
        </DialogDescription>

        {/* Mobile tab bar */}
        <div className="flex items-center gap-1 border-b border-border bg-background/95 p-2 md:hidden">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <Button
                key={s.id}
                variant={section === s.id ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  'flex-1 min-h-[44px] flex-col gap-0.5 px-1 py-2',
                  s.id === 'danger' && section !== s.id && 'text-destructive'
                )}
                onClick={() => setSection(s.id)}
              >
                <Icon className="h-4 w-4" />
                <span className="text-[10px] leading-none">{s.label}</span>
              </Button>
            );
          })}
          <Button
            variant="ghost"
            size="icon"
            className="min-h-[44px] min-w-[44px] shrink-0"
            onClick={handleCloseClick}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Desktop sidebar */}
        <aside className="hidden h-full flex-col border-r border-border bg-background/95 p-4 md:flex">
          <div className="mb-4 px-2 text-lg font-semibold">Account</div>
          <nav className="flex flex-1 flex-col gap-1">
            {sections.map((s) => {
              const Icon = s.icon;
              return (
                <Button
                  key={s.id}
                  variant={section === s.id ? 'default' : 'ghost'}
                  className={cn(
                    'justify-start gap-2 min-h-[44px]',
                    s.id === 'danger' && section !== s.id && 'text-destructive'
                  )}
                  onClick={() => setSection(s.id)}
                >
                  <Icon className="h-4 w-4" />
                  {s.label}
                </Button>
              );
            })}
          </nav>
        </aside>

        {/* Content area */}
        <div className="relative flex min-h-0 flex-col overflow-hidden">
          {/* Desktop-only close button top-right */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 z-10 hidden min-h-[44px] min-w-[44px] md:flex"
            onClick={handleCloseClick}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </Button>
          <div
            className="flex-1 overflow-y-auto px-4 py-6 md:px-10 md:py-10"
            style={{
              paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            }}
          >
            {section === 'danger' && (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive md:hidden">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Careful — actions here cannot be undone.</span>
              </div>
            )}
            <div className={sectionWrapClass('profile')}>
              <ProfileSection ref={profileRef} />
            </div>
            <div className={sectionWrapClass('email')}>
              <EmailSection onClose={() => onOpenChange(false)} />
            </div>
            <div className={sectionWrapClass('password')}>
              {hasPasswordProvider && <PasswordSection />}
            </div>
            <div className={sectionWrapClass('passkeys')}>
              <PasskeysSection />
            </div>
            <div className={sectionWrapClass('invite')}>
              <InviteFriendsSection />
            </div>
            <div className={sectionWrapClass('danger')}>
              <DangerSection onDeleted={() => onOpenChange(false)} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
